import { useEffect, useState, forwardRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PricingCard } from "@/components/pricing/PricingCard";
import { useSystemPlans } from "@/hooks/use-plans";
import FleetSubscriptionModal from "@/components/fleet/FleetSubscriptionModal";
import { FleetTrialModal } from "@/components/fleet/FleetTrialModal";
import { FleetDemoModal } from "@/components/fleet/FleetDemoModal";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { FleetSystemDemo } from "@/components/fleet/FleetSystemDemo";
import { PublicSystemPlanCard } from "@/components/plans/PublicSystemPlanCard";
import { FleetLoginDropdown } from "@/components/fleet/FleetLoginDropdown";
import { ForgotPasswordModal } from "@/components/ForgotPasswordModal";
import { ThemeToggle } from "@/components/theme-toggle";
import { FleetLogo } from "@/components/fleet/FleetLogo";
import portalOneLogo from "@/assets/portalone-suite-logo.png";
import { useIsMobile } from "@/hooks/use-mobile";
import { 
  Truck, 
  MapPin, 
  Fuel, 
  Wrench, 
  BarChart3, 
  Shield, 
  Clock,
  CheckCircle,
  TrendingUp,
  Users,
  FileText,
  Smartphone,
  ArrowRight,
  Menu,
  X
} from "lucide-react";

// Paleta de cores FleetONE baseada no novo logo
const fleetBrand = {
  teal: "#4A9B8E",
  tealLight: "#5AADA0",
  aqua: "#82C5BB",
  tealDark: "#3A8A7D",
  graphite: "#111111",
  fog: "#E5E5E5",
};

const fadeIn = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

// Componente GlowHalo customizado para FleetONE
function FleetGlowHalo({ className = "" }: { className?: string }) {
  return (
    <div className={`pointer-events-none absolute inset-0 -z-10 blur-3xl ${className}`} aria-hidden>
      <div
        className="absolute -top-24 -left-24 h-72 w-72 rounded-full"
        style={{ 
          background: `radial-gradient(60% 60% at 50% 50%, ${fleetBrand.teal}66 0%, transparent 65%)`,
        }}
      />
      <div
        className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full"
        style={{ 
          background: `radial-gradient(60% 60% at 50% 50%, ${fleetBrand.aqua}66 0%, transparent 65%)`,
        }}
      />
    </div>
  );
}

// Botão Neon customizado para FleetONE
function FleetNeonButton({ children, className = "", ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={`group relative overflow-hidden rounded-2xl px-5 py-3 font-medium text-white shadow-md transition hover:shadow-lg ${className}`}
      style={{
        background: `linear-gradient(90deg, ${fleetBrand.teal}, ${fleetBrand.aqua})`,
        boxShadow: `0 0 30px ${fleetBrand.teal}44, 0 0 50px ${fleetBrand.aqua}33`,
      }}
      {...props}
    >
      <span className="relative z-10 flex items-center gap-2">
        {children}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </span>
      <span
        className="absolute inset-0 -z-0 opacity-0 transition-opacity group-hover:opacity-20"
        style={{ background: `radial-gradient(80% 80% at 50% 50%, white, transparent)` }}
      />
    </button>
  );
}

// Botão de Login customizado
const FleetLoginButton = forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ children, ...props }, ref) => (
    <button
      ref={ref}
      className="group relative overflow-hidden rounded-2xl px-5 py-3 font-semibold text-white shadow-md transition hover:shadow-lg focus:outline-none"
      style={{ 
        background: `linear-gradient(90deg, ${fleetBrand.teal}, ${fleetBrand.aqua})`, 
        boxShadow: `0 0 30px ${fleetBrand.teal}44, 0 0 50px ${fleetBrand.aqua}33` 
      }}
      {...props}
    >
      <span className="relative z-10 inline-flex items-center gap-2">
        {children}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5"/>
      </span>
      <span 
        className="absolute inset-0 -z-0 opacity-0 transition-opacity group-hover:opacity-20" 
        style={{ background: `radial-gradient(80% 80% at 50% 50%, white, transparent)` }} 
      />
    </button>
  )
);
FleetLoginButton.displayName = "FleetLoginButton";

const fleetFeatures = [
  {
    icon: Truck,
    title: "Gestão Completa da Frota",
    description: "Controle total de veículos, motoristas e documentos em um só lugar"
  },
  {
    icon: MapPin,
    title: "Rastreamento em Tempo Real",
    description: "Monitoramento GPS com telemetria avançada e alertas inteligentes"
  },
  {
    icon: Fuel,
    title: "Controle de Combustível",
    description: "Acompanhe consumo, custos e eficiência energética da frota"
  },
  {
    icon: Wrench,
    title: "Manutenção Preventiva",
    description: "Planos de manutenção automatizados para reduzir custos e paradas"
  },
  {
    icon: BarChart3,
    title: "Relatórios Inteligentes",
    description: "Analytics avançados para otimização e tomada de decisões"
  },
  {
    icon: Shield,
    title: "Conformidade e Segurança",
    description: "Gestão de CNH, documentos e compliance regulatório"
  }
];

const fleetBenefits = [
  "Reduza custos operacionais em até 30%",
  "Melhore a eficiência da frota",
  "Garantia de conformidade regulatória",
  "Relatórios gerenciais em tempo real",
  "Integração com sistemas existentes",
  "Suporte técnico especializado 24/7"
];

const testimonials = [
  {
    company: "Transportadora Silva",
    author: "João Silva",
    role: "Diretor de Operações",
    content: "O FleetONE revolucionou nossa operação. Conseguimos reduzir 35% dos custos de combustível e melhorar drasticamente o controle da frota.",
    logo: "🚛",
    results: "35% menos custos"
  },
  {
    company: "Logística Moderna", 
    author: "Maria Santos",
    role: "Gerente de Frota",
    content: "Excelente sistema! O rastreamento em tempo real e os relatórios nos dão total visibilidade sobre nossa operação.",
    logo: "📦",
    results: "100% visibilidade"
  },
  {
    company: "Delivery Express",
    author: "Carlos Oliveira", 
    role: "CEO",
    content: "Com o FleetONE conseguimos otimizar nossas rotas e reduzir o tempo de entrega em 40%. ROI em menos de 6 meses.",
    logo: "🚚",
    results: "40% menos tempo"
  }
];

export default function FleetLandingPage() {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [trialModalOpen, setTrialModalOpen] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { data: plans, isLoading } = useSystemPlans('fleet');
  const location = useLocation();
  const isMobile = useIsMobile();

  // Handle hash navigation
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-300 selection:bg-primary/20 overflow-x-hidden">
      {/* Top glow backdrop */}
      <FleetGlowHalo />

      {/* Header Responsivo */}
      <header className="sticky top-0 z-40 w-full backdrop-blur-sm bg-background/80 border-b border-border transition-colors duration-300">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 md:px-6 py-3 md:py-4">
          {/* Logo - Icon em mobile, completo em desktop */}
          <Link to="/" className="flex items-center cursor-pointer">
            <FleetLogo 
              variant={isMobile ? 'icon' : 'full'}
              alt="FleetONE - Gestão Inteligente de Frotas" 
              className={isMobile ? "h-10 w-10 object-contain" : "h-16 md:h-20 w-auto object-contain"}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-7 md:flex">
            {[
              ["Início", "#top"],
              ["Recursos", "#recursos"],
              ["Benefícios", "#beneficios"],
              ["Planos", "#planos"],
              ["Contato", "/contact"],
            ].map(([label, href]) => (
              <a 
                key={label} 
                href={href as string} 
                onClick={(e) => {
                  if ((href as string).startsWith('#')) {
                    e.preventDefault();
                    const element = document.querySelector(href as string);
                    element?.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {label}
              </a>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            <ThemeToggle />
            <FleetLoginDropdown
              onForgotPassword={() => setShowForgotPassword(true)}
              onSignup={() => setSubscriptionModalOpen(true)}
              triggerButton={<FleetLoginButton>Login</FleetLoginButton>}
            />
          </div>

          {/* Mobile Actions */}
          <div className="flex md:hidden items-center gap-2">
            <ThemeToggle />
            
            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
              aria-label="Menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" style={{ color: fleetBrand.teal }} />
              ) : (
                <Menu className="h-6 w-6" style={{ color: fleetBrand.teal }} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden border-t border-border bg-background/95 backdrop-blur-sm"
          >
            <div className="px-4 py-4 space-y-3">
              {[
                ["Início", "#top"],
                ["Recursos", "#recursos"],
                ["Benefícios", "#beneficios"],
                ["Planos", "#planos"],
                ["Contato", "/contact"],
              ].map(([label, href]) => (
                <a
                  key={label}
                  href={href as string}
                  onClick={(e) => {
                    if ((href as string).startsWith('#')) {
                      e.preventDefault();
                      const element = document.querySelector(href as string);
                      element?.scrollIntoView({ behavior: 'smooth' });
                    }
                    setMobileMenuOpen(false);
                  }}
                  className="block py-2 text-base text-muted-foreground hover:text-foreground transition-colors"
                >
                  {label}
                </a>
              ))}
              
              {/* Login no menu mobile */}
              <div className="border-t border-border pt-4 mt-4">
                <FleetLoginDropdown
                  onForgotPassword={() => {
                    setMobileMenuOpen(false);
                    setShowForgotPassword(true);
                  }}
                  onSignup={() => {
                    setMobileMenuOpen(false);
                    setSubscriptionModalOpen(true);
                  }}
                  triggerButton={
                    <button className="w-full py-2 px-4 rounded-lg text-white font-medium"
                      style={{ background: `linear-gradient(90deg, ${fleetBrand.teal}, ${fleetBrand.aqua})` }}
                    >
                      Login
                    </button>
                  }
                />
              </div>
            </div>
          </motion.nav>
        )}
      </header>
      
      {/* Hero Section com nova identidade visual */}
      <section id="top" className="relative py-20 px-4 overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            background: `radial-gradient(120% 80% at 0% 0%, ${fleetBrand.teal}55 0%, transparent 55%), 
                         radial-gradient(120% 80% at 100% 100%, ${fleetBrand.aqua}55 0%, transparent 55%)`,
          }}
        />
        
        <div className="container relative mx-auto max-w-6xl text-center">
          <motion.div variants={fadeIn} initial="hidden" animate="show">
            <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold text-foreground mb-4 md:mb-6">
              Gestão Inteligente 
              <span className="block md:inline md:ml-3" style={{ color: fleetBrand.teal }}>
                de Frotas
              </span>
            </h1>
            
            <p className="text-base md:text-xl text-muted-foreground mb-6 md:mb-8 max-w-2xl mx-auto leading-relaxed px-4">
              Controle total, eficiência máxima e conformidade garantida para sua operação logística
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 mb-8 md:mb-12 w-full sm:w-auto px-4 sm:px-0">
              <FleetNeonButton 
                onClick={() => setSubscriptionModalOpen(true)}
                className="text-base md:text-lg px-6 md:px-8 w-full sm:w-auto"
              >
                Começar Agora
              </FleetNeonButton>
              
              <Button 
                size="lg" 
                variant="outline" 
                className="text-base md:text-lg px-6 md:px-8 rounded-2xl border-2 hover:bg-card w-full sm:w-auto"
                style={{
                  borderColor: fleetBrand.teal,
                  color: fleetBrand.teal
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = fleetBrand.teal;
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = fleetBrand.teal;
                }}
                onClick={() => setTrialModalOpen(true)}
              >
                Teste Grátis 30 Dias
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="text-base md:text-lg px-6 md:px-8 rounded-2xl border-2 hover:bg-card w-full sm:w-auto"
                style={{
                  borderColor: fleetBrand.teal,
                  color: fleetBrand.teal
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = fleetBrand.teal;
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = fleetBrand.teal;
                }}
                onClick={() => setDemoModalOpen(true)}
              >
                Agendar Demo
              </Button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 max-w-4xl mx-auto px-4">
              {[
                { icon: Users, text: "500+", description: "Empresas Ativas" },
                { icon: Truck, text: "5000+", description: "Veículos Gerenciados" },
                { icon: TrendingUp, text: "30%", description: "Redução de Custos" },
                { icon: CheckCircle, text: "99.9%", description: "Disponibilidade" }
              ].map((item, index) => (
                <div 
                  key={index} 
                  className="flex flex-col items-center gap-2 p-4 md:p-6 rounded-2xl backdrop-blur-sm bg-card/30"
                  style={{ 
                    borderColor: fleetBrand.teal,
                    borderWidth: '2px'
                  }}
                >
                  <item.icon className="w-6 h-6 md:w-8 md:h-8 mb-1 md:mb-2" style={{ color: fleetBrand.teal }} />
                  <span className="text-xl md:text-3xl font-bold">{item.text}</span>
                  <span className="text-xs md:text-sm text-muted-foreground text-center">{item.description}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Fleet System Demo */}
      <FleetSystemDemo />

      {/* Features Section */}
      <section id="recursos" className="py-12 md:py-16 lg:py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 md:mb-4">
              Funcionalidades Completas para sua Frota
            </h2>
            <p className="text-base md:text-xl text-muted-foreground max-w-3xl mx-auto px-4">
              Tudo que você precisa para uma gestão eficiente da sua frota, com tecnologia de ponta 
              e integração total entre todos os módulos
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
            {fleetFeatures.map((feature, index) => (
              <Card 
                key={index} 
                className="group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border-0 bg-gradient-to-br from-card to-card/50 p-4 md:p-0"
                style={{
                  boxShadow: '0 0 0 1px rgba(74, 155, 142, 0.1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = `0 20px 40px ${fleetBrand.teal}20`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 0 0 1px rgba(74, 155, 142, 0.1)';
                }}
              >
                <CardHeader>
                  <div 
                    className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-300"
                    style={{ 
                      background: `linear-gradient(135deg, ${fleetBrand.teal}22, ${fleetBrand.aqua}22)` 
                    }}
                  >
                    <feature.icon className="w-6 h-6 md:w-7 md:h-7" style={{ color: fleetBrand.teal }} />
                  </div>
                  <CardTitle className="text-lg md:text-xl group-hover:text-primary transition-colors">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm md:text-base leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Como Funciona o FleetONE
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Processo simples em 4 etapas para transformar sua gestão de frota
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Cadastro Inicial",
                description: "Configure sua empresa, usuários e perfis de acesso em minutos",
                icon: Users
              },
              {
                step: "02", 
                title: "Importação de Dados",
                description: "Importe sua frota, motoristas e documentos de forma automatizada",
                icon: Truck
              },
              {
                step: "03",
                title: "Configuração",
                description: "Defina alertas, planos de manutenção e parâmetros operacionais",
                icon: Wrench
              },
              {
                step: "04",
                title: "Operação",
                description: "Monitore em tempo real e acesse relatórios gerenciais avançados",
                icon: BarChart3
              }
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{ 
                      background: `linear-gradient(135deg, ${fleetBrand.teal}, ${fleetBrand.tealLight})` 
                    }}
                  >
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                  <div 
                    className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: fleetBrand.aqua }}
                  >
                    <span className="text-sm font-bold text-white">{step.step}</span>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="beneficios" className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Por que escolher o FleetONE?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Resultados comprovados que transformam negócios
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Benefits List */}
            <div className="space-y-6">
              {fleetBenefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4 p-4 rounded-lg hover:bg-muted/20 transition-colors">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" style={{ color: fleetBrand.teal }} />
                  <div>
                    <span className="text-lg font-medium">{benefit}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* ROI Calculator */}
            <div 
              className="p-8 rounded-2xl border"
              style={{ 
                background: `linear-gradient(135deg, ${fleetBrand.teal}11, ${fleetBrand.aqua}11)`,
                borderColor: fleetBrand.tealLight
              }}
            >
              <h3 className="text-2xl font-bold mb-6">Calcule seu ROI</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-card p-4 rounded-lg">
                    <div className="text-2xl font-bold" style={{ color: fleetBrand.teal }}>30%</div>
                    <div className="text-sm text-muted-foreground">Redução de Custos</div>
                  </div>
                  <div className="bg-card p-4 rounded-lg">
                    <div className="text-2xl font-bold" style={{ color: fleetBrand.aqua }}>6 meses</div>
                    <div className="text-sm text-muted-foreground">Retorno do Investimento</div>
                  </div>
                </div>
                <div className="bg-card p-4 rounded-lg">
                  <div className="text-lg font-semibold mb-2">Economia mensal estimada:</div>
                  <div className="text-3xl font-bold" style={{ color: fleetBrand.teal }}>R$ 2.500</div>
                  <div className="text-sm text-muted-foreground">*Para frotas de 10+ veículos</div>
                </div>
                <FleetNeonButton className="w-full">
                  Calcular meu ROI
                </FleetNeonButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Soluções para Cada Tipo de Operação
            </h2>
            <p className="text-xl text-muted-foreground">
              Flexibilidade total para atender diferentes segmentos
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Transporte de Cargas",
                description: "Gestão completa para transportadoras e operadores logísticos",
                icon: Truck,
                features: ["Rastreamento de cargas", "Otimização de rotas", "Controle de prazos"]
              },
              {
                title: "Delivery e E-commerce",
                description: "Soluções específicas para entrega rápida e gestão urbana",
                icon: MapPin,
                features: ["Roteirização inteligente", "Proof of delivery", "Tempo real para clientes"]
              },
              {
                title: "Frotas Corporativas",
                description: "Controle administrativo e operacional para empresas",
                icon: Users,
                features: ["Política de uso", "Controle de gastos", "Relatórios executivos"]
              }
            ].map((useCase, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                    style={{ 
                      background: `linear-gradient(135deg, ${fleetBrand.teal}20, ${fleetBrand.aqua}20)` 
                    }}
                  >
                    <useCase.icon className="w-7 h-7" style={{ color: fleetBrand.teal }} />
                  </div>
                  <CardTitle className="text-xl">{useCase.title}</CardTitle>
                  <CardDescription className="text-base">{useCase.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {useCase.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" style={{ color: fleetBrand.teal }} />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="planos" className="py-12 md:py-16 lg:py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 md:mb-4">
              Planos que se adaptam ao seu negócio
            </h2>
            <p className="text-base md:text-xl text-muted-foreground mb-6 md:mb-8 px-4">
              Escolha o plano ideal para o tamanho da sua frota
            </p>
            
            <div className="inline-flex items-center bg-muted rounded-lg p-1">
              <Button
                variant={billingPeriod === 'monthly' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setBillingPeriod('monthly')}
                style={billingPeriod === 'monthly' ? {
                  background: `linear-gradient(90deg, ${fleetBrand.teal}, ${fleetBrand.aqua})`,
                  color: 'white'
                } : {}}
              >
                Mensal
              </Button>
              <Button
                variant={billingPeriod === 'yearly' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setBillingPeriod('yearly')}
                style={billingPeriod === 'yearly' ? {
                  background: `linear-gradient(90deg, ${fleetBrand.teal}, ${fleetBrand.aqua})`,
                  color: 'white'
                } : {}}
              >
                Anual
                <Badge variant="secondary" className="ml-2">-15%</Badge>
              </Button>
            </div>
          </div>
          
          {isLoading ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="p-6">
                  <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-muted rounded" />
                    <div className="h-12 bg-muted rounded" />
                    <div className="space-y-2">
                      {[1, 2, 3, 4].map((j) => (
                        <div key={j} className="h-4 bg-muted rounded" />
                      ))}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8 max-w-7xl mx-auto px-4">
              {plans?.filter(plan => !/trial/i.test(plan.name)).map((plan, index) => (
                <PublicSystemPlanCard
                  key={plan.id}
                  plan={plan}
                  systemCode="fleet"
                  billingPeriod={billingPeriod}
                  onSelect={(planId, period) => {
                    console.log('Selected plan:', planId, period);
                    setSubscriptionModalOpen(true);
                  }}
                  brandColors={{
                    primary: fleetBrand.teal,
                    secondary: fleetBrand.tealLight,
                    accent: fleetBrand.aqua
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 md:py-16 lg:py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 md:mb-4">
              Resultados Reais de Clientes Reais
            </h2>
            <p className="text-base md:text-xl text-muted-foreground px-4">
              Veja como o FleetONE está transformando negócios
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8 px-4">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                <div 
                  className="absolute top-0 right-0 px-2 md:px-3 py-1 rounded-bl-lg"
                  style={{ 
                    background: `linear-gradient(135deg, ${fleetBrand.teal}40, ${fleetBrand.aqua}30)` 
                  }}
                >
                  <span className="text-xs font-semibold" style={{ color: fleetBrand.teal }}>
                    {testimonial.results}
                  </span>
                </div>
                <CardContent className="p-4 md:p-6 space-y-3 md:space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl md:text-3xl">{testimonial.logo}</div>
                    <div>
                      <div className="font-bold text-base md:text-lg">{testimonial.company}</div>
                      <div className="text-xs md:text-sm text-muted-foreground">
                        {testimonial.author} - {testimonial.role}
                      </div>
                    </div>
                  </div>
                  <blockquote 
                    className="text-sm md:text-base text-muted-foreground leading-relaxed border-l-4 pl-3 md:pl-4 italic"
                    style={{ borderColor: `${fleetBrand.teal}30` }}
                  >
                    "{testimonial.content}"
                  </blockquote>
                  <div className="flex items-center gap-1 md:gap-2 pt-2">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="w-3 h-3 md:w-4 md:h-4 text-yellow-400">⭐</div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Integração Total com seus Sistemas
          </h2>
          <p className="text-xl text-muted-foreground mb-12">
            Conecte o FleetONE com suas ferramentas existentes
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            {[
              { name: "ERP", icon: "⚙️" },
              { name: "Telemetria", icon: "📡" },
              { name: "Combustível", icon: "⛽" },
              { name: "API Rest", icon: "🔗" }
            ].map((integration, index) => (
              <div 
                key={index} 
                className="flex flex-col items-center gap-3 p-6 bg-card rounded-xl hover:shadow-lg transition-all"
                style={{
                  boxShadow: `0 0 0 1px ${fleetBrand.tealLight}22`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = `0 10px 30px ${fleetBrand.teal}30`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = `0 0 0 1px ${fleetBrand.tealLight}22`;
                }}
              >
                <div className="text-4xl">{integration.icon}</div>
                <span className="font-medium">{integration.name}</span>
              </div>
            ))}
          </div>
          
          <Button size="lg" variant="outline" className="rounded-2xl">
            Ver Todas as Integrações
          </Button>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 px-4"
        style={{
          background: `linear-gradient(135deg, ${fleetBrand.teal}, ${fleetBrand.aqua})`
        }}
      >
        <div className="container mx-auto max-w-4xl text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Pronto para transformar sua frota?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Comece seu teste gratuito hoje mesmo e veja a diferença
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              variant="secondary" 
              className="text-lg px-8 rounded-2xl"
              onClick={() => setTrialModalOpen(true)}
            >
              <Clock className="w-5 h-5 mr-2" />
              Teste Grátis 30 Dias
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="text-lg px-8 border-white text-white hover:bg-white rounded-2xl"
              style={{
                borderColor: 'white',
                color: 'white'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = fleetBrand.teal;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'white';
              }}
            >
              Falar com Especialista
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-muted/30 transition-colors duration-300">
        <div className="mx-auto max-w-7xl px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Logo à esquerda */}
            <div className="flex-shrink-0">
              <Link to="/" className="cursor-pointer">
                <img src={portalOneLogo} alt="PortalONE Suite" className="h-[60px] md:h-[70px] w-auto rounded-sm object-contain" />
              </Link>
            </div>

            {/* Copyright centralizado */}
            <div className="flex-1 text-center">
              <span className="text-xs md:text-sm text-muted-foreground">
                © {new Date().getFullYear()} — Todos os direitos reservados.
              </span>
            </div>

            {/* Links à direita */}
            <div className="flex gap-6 text-sm text-muted-foreground flex-shrink-0">
              <a href="/legal/terms" className="hover:text-foreground transition-colors whitespace-nowrap">Termos</a>
              <a href="/legal/privacy" className="hover:text-foreground transition-colors whitespace-nowrap">Privacidade</a>
              <a href="/status" className="hover:text-foreground transition-colors whitespace-nowrap">Status</a>
            </div>
          </div>
        </div>
      </footer>
      
      {/* WhatsApp Float */}
      <WhatsAppFloat systemName="FleetONE" systemCode="fleet" />
      
      {/* Modals */}
      <FleetSubscriptionModal 
        open={subscriptionModalOpen}
        onOpenChange={setSubscriptionModalOpen}
      />
      
      <FleetTrialModal 
        open={trialModalOpen}
        onOpenChange={setTrialModalOpen}
      />
      
      <FleetDemoModal 
        open={demoModalOpen}
        onOpenChange={setDemoModalOpen}
      />

      <ForgotPasswordModal 
        open={showForgotPassword}
        onOpenChange={setShowForgotPassword}
      />
    </div>
  );
}
